export class Header {
  static FONT_STORAGE_KEY = 'ube-font-size'
  static DYSLEXIA_STORAGE_KEY = 'ube-dyslexia'
  static FONT_STEPS = [14, 16, 18, 20]
  static FONT_DEFAULT = 16
  static MOBILE_MAX = 769

  constructor() {
    this._header = document.querySelector('.h-wrap')
    this._hero = document.querySelector('.s-hero')
    this._burger = document.querySelector('#burger')
    this._mobileMenu = document.querySelector('#mobile-menu')
    this._overlay = document.querySelector('.mobile-menu__overlay')
    this._searchToggle = document.querySelector('#open-search')
    this._searchModal = document.querySelector('#search-modal')
    this._searchClose = document.querySelector('#close-search')
    this._searchInput = document.querySelector('#h-search-input')
    this._a11y = document.querySelector('.h-a11y')
    this._a11yToggle = document.querySelector('#open-a11y')
    this._a11ySheet = document.querySelector('#a11y-modal')
    this._quickToggle = document.querySelector('#open-quick')
    this._quickSheet = document.querySelector('#quick-modal')
    this._sheets = [this._a11ySheet, this._quickSheet].filter(Boolean)
    this._mobileMq = window.matchMedia(`(max-width: ${Header.MOBILE_MAX}px)`)
    this._scrollValue = 0
    this._fontSize = Header.FONT_DEFAULT

    if (this._hero && this._header) {
      this._heroHeight = this._hero.clientHeight
      this.scroll()
    }

    if (this._burger && this._mobileMenu) {
      this._burger.addEventListener('click', this.handleBurger)
      this._overlay?.addEventListener('click', this.closeMobileMenu)
      this.setupMobileAccordions()
      this.updateMobileMenuOffset()
      window.addEventListener('resize', this.updateMobileMenuOffset)
    }

    if (this._searchModal) {
      this._searchToggle?.addEventListener('click', this.openSearch)
      this._searchClose?.addEventListener('click', this.closeSearch)
      this._searchModal.querySelector('[data-close-search]')?.addEventListener('click', this.closeSearch)
    }

    if (this._a11y) {
      this._a11y.addEventListener('click', this.handleA11yClick)
      this.initFontSize()
      this.initDyslexia()
    }

    this._a11yToggle?.addEventListener('click', () => this.toggleTopSheet(this._a11ySheet))
    this._quickToggle?.addEventListener('click', () => this.toggleTopSheet(this._quickSheet))
    this._sheets.forEach((sheet) => {
      sheet.querySelectorAll('[data-close-sheet]').forEach((el) => {
        el.addEventListener('click', () => this.closeTopSheet(sheet))
      })
    })
    this._mobileMq.addEventListener('change', this.handleMobileMq)

    if (this._burger || this._searchModal || this._sheets.length) {
      document.addEventListener('keydown', this.handleKeydown)
    }

    if (this._header) {
      window.addEventListener('scroll', this.scroll)
    }
  }

  handleBurger = () => {
    if (this._mobileMenu.classList.contains('is-open')) {
      this.closeMobileMenu()
      return
    }

    this.openMobileMenu()
  }

  openMobileMenu = () => {
    this.closeSearch()
    this.closeTopSheets()
    this.updateMobileMenuOffset()
    this._header?.classList.add('is-mobile-menu-open')
    this._mobileMenu.classList.add('is-open')
    this._mobileMenu.setAttribute('aria-hidden', 'false')
    this._overlay?.classList.add('is-open')
    this._overlay?.setAttribute('aria-hidden', 'false')
    this._burger.setAttribute('aria-expanded', 'true')
    // ponytail: labels traduits via data-label-* (Blade / __)
    this._burger.setAttribute('aria-label', this._burger.dataset.labelClose || '')
    document.body.classList.add('mobile-menu-open')
  }

  closeMobileMenu = () => {
    if (!this._mobileMenu?.classList.contains('is-open')) {
      return
    }

    this._header?.classList.remove('is-mobile-menu-open')
    this._mobileMenu.classList.remove('is-open')
    this._mobileMenu.setAttribute('aria-hidden', 'true')
    this._overlay?.classList.remove('is-open')
    this._overlay?.setAttribute('aria-hidden', 'true')
    this._burger?.setAttribute('aria-expanded', 'false')
    this._burger?.setAttribute('aria-label', this._burger?.dataset.labelOpen || '')
    document.body.classList.remove('mobile-menu-open')
    this.closeAllAccordions()
  }

  openSearch = () => {
    if (!this._searchModal) {
      return
    }

    this.closeMobileMenu()
    this.closeTopSheets()
    this._searchModal.classList.add('is-open')
    this._searchModal.setAttribute('aria-hidden', 'false')
    this._searchToggle?.setAttribute('aria-expanded', 'true')
    document.body.classList.add('search-modal-open')
    // ponytail: focus après paint pour coller à l’ouverture du panneau
    requestAnimationFrame(() => this._searchInput?.focus())
  }

  handleA11yClick = (event) => {
    const button = event.target.closest('[data-a11y]')
    if (!button || !this._a11y.contains(button)) {
      return
    }

    switch (button.dataset.a11y) {
      case 'menu':
        this.focusMainMenu()
        break
      case 'search':
        this.openSearch()
        break
      case 'dyslexia':
        this.toggleDyslexia()
        break
      case 'font-down':
        this.setFontSize(this._fontSize - 2)
        break
      case 'font-reset':
        this.setFontSize(Header.FONT_DEFAULT)
        break
      case 'font-up':
        this.setFontSize(this._fontSize + 2)
        break
      default:
        break
    }
  }

  focusMainMenu = () => {
    this.closeTopSheets()
    const firstItem = this._header?.querySelector('.h-nav__nav .menu > .menu-item > a')
    firstItem?.focus()
  }

  initFontSize = () => {
    const stored = Number(localStorage.getItem(Header.FONT_STORAGE_KEY))
    const next = Header.FONT_STEPS.includes(stored) ? stored : Header.FONT_DEFAULT
    this.setFontSize(next, false)
  }

  setFontSize = (size, persist = true) => {
    const steps = Header.FONT_STEPS
    const clamped = steps.reduce((closest, step) => (
      Math.abs(step - size) < Math.abs(closest - size) ? step : closest
    ), steps[0])

    this._fontSize = clamped
    document.documentElement.style.fontSize = `${clamped}px`

    if (persist) {
      localStorage.setItem(Header.FONT_STORAGE_KEY, String(clamped))
    }

    this._a11y?.querySelector('[data-a11y="font-down"]')?.toggleAttribute('disabled', clamped === steps[0])
    this._a11y?.querySelector('[data-a11y="font-up"]')?.toggleAttribute('disabled', clamped === steps[steps.length - 1])
  }

  initDyslexia = () => {
    this.setDyslexia(localStorage.getItem(Header.DYSLEXIA_STORAGE_KEY) === '1', false)
  }

  toggleDyslexia = () => {
    this.setDyslexia(!document.documentElement.classList.contains('is-dyslexia'))
  }

  setDyslexia = (on, persist = true) => {
    document.documentElement.classList.toggle('is-dyslexia', on)
    this._a11y?.querySelector('[data-a11y="dyslexia"]')?.setAttribute('aria-pressed', String(on))

    if (persist) {
      localStorage.setItem(Header.DYSLEXIA_STORAGE_KEY, on ? '1' : '0')
    }
  }

  closeSearch = () => {
    if (!this._searchModal?.classList.contains('is-open')) {
      return
    }

    this._searchModal.classList.remove('is-open')
    this._searchModal.setAttribute('aria-hidden', 'true')
    this._searchToggle?.setAttribute('aria-expanded', 'false')
    document.body.classList.remove('search-modal-open')
    this._searchToggle?.focus()
  }

  handleKeydown = (event) => {
    if (event.key !== 'Escape') {
      return
    }

    const openSheet = this._sheets.find((sheet) => sheet.classList.contains('is-open'))
    if (openSheet) {
      this.closeTopSheet(openSheet)
      return
    }

    if (this._searchModal?.classList.contains('is-open')) {
      this.closeSearch()
      return
    }

    if (this._mobileMenu?.classList.contains('is-open')) {
      this.closeMobileMenu()
    }
  }

  sheetToggle = (sheet) => {
    if (sheet === this._a11ySheet) {
      return this._a11yToggle
    }

    if (sheet === this._quickSheet) {
      return this._quickToggle
    }

    return null
  }

  toggleTopSheet = (sheet) => {
    if (sheet?.classList.contains('is-open')) {
      this.closeTopSheet(sheet)
      return
    }

    this.openTopSheet(sheet)
  }

  openTopSheet = (sheet) => {
    if (!sheet || sheet.classList.contains('is-open')) {
      return
    }

    this.closeSearch()
    this.closeMobileMenu()
    this._sheets.forEach((other) => {
      if (other !== sheet) {
        this.closeTopSheet(other, false)
      }
    })

    const toggle = this.sheetToggle(sheet)
    const dialog = sheet.querySelector('.h-top__sheet-dialog')
    const title = sheet.querySelector('.h-top__sheet-title')

    sheet.classList.add('is-open')
    dialog?.setAttribute('role', 'dialog')
    dialog?.setAttribute('aria-modal', 'true')
    if (title?.id) {
      dialog?.setAttribute('aria-labelledby', title.id)
    }
    toggle?.setAttribute('aria-expanded', 'true')
    document.body.classList.add('h-top-sheet-open')

    requestAnimationFrame(() => {
      const first = dialog?.querySelector('button:not([data-close-sheet]), a')
      first?.focus()
    })
  }

  closeTopSheet = (sheet, restoreFocus = true) => {
    if (!sheet?.classList.contains('is-open')) {
      return
    }

    const toggle = this.sheetToggle(sheet)
    const dialog = sheet.querySelector('.h-top__sheet-dialog')

    sheet.classList.remove('is-open')
    dialog?.removeAttribute('role')
    dialog?.removeAttribute('aria-modal')
    dialog?.removeAttribute('aria-labelledby')
    toggle?.setAttribute('aria-expanded', 'false')

    if (!this._sheets.some((item) => item.classList.contains('is-open'))) {
      document.body.classList.remove('h-top-sheet-open')
    }

    if (restoreFocus) {
      toggle?.focus()
    }
  }

  closeTopSheets = () => {
    this._sheets.forEach((sheet) => this.closeTopSheet(sheet, false))
  }

  handleMobileMq = () => {
    if (!this._mobileMq.matches) {
      this.closeTopSheets()
    }
  }

  updateMobileMenuOffset = () => {
    if (!this._header) {
      return
    }

    document.documentElement.style.setProperty('--h-wrap-height', `${this._header.offsetHeight}px`)
  }

  setupMobileAccordions = () => {
    this._mobileMenu.querySelectorAll('.menu > .menu-item-has-children > a').forEach((link) => {
      link.addEventListener('click', this.handleAccordionClick)
    })
  }

  handleAccordionClick = (event) => {
    const link = event.currentTarget
    const item = link.closest('.menu-item')
    if (!item) {
      return
    }

    const isOpen = item.classList.contains('is-open')
    const href = link.getAttribute('href')
    const hasUrl = href && href !== '#' && !href.endsWith('#')

    if (isOpen) {
      if (hasUrl) {
        return
      }

      event.preventDefault()
      item.classList.remove('is-open')
      return
    }

    event.preventDefault()
    this.closeAllAccordions()
    item.classList.add('is-open')
  }

  closeAllAccordions = () => {
    this._mobileMenu?.querySelectorAll('.menu > .menu-item.is-open').forEach((item) => {
      item.classList.remove('is-open')
    })
  }

  scroll = () => {
    if (!this._header || !this._hero) {
      return
    }

    this._scrollValue = window.scrollY

    if (this._scrollValue > this._heroHeight) {
      this._header.classList.add('scrolled')
    } else {
      this._header.classList.remove('scrolled')
    }
  }
}
